import type { BayHireRequest } from "@services/bayHireApi";
import { daysUntilExpiry } from "./bayHireExpiry";

// DFT-0016 (DFE-BAY-HIRE-004): the renewal pre-window, in days before expiry. The
// SAME boundary drives DFT-0019's expiry-reminder task, per the requester's
// 2026-08-04 decision that the reminder fires exactly when this button first
// becomes visible - keep this as the one definition either consumer imports,
// never a duplicated literal (working/07-technical-approach.md D2).
export const RENEWAL_WINDOW_DAYS = 30;

/**
 * DFT-0044 slice F (F2): shared visibility rules for the Vendor's own-request actions, extracted
 * so `BayHireRequestDetail` (header buttons) and `BayHireRequestList` (row kebab, F2) cannot drift
 * apart on when each action is offered - the same failure mode D43's `bayAssignments` immutability
 * check exists to prevent on the backend.
 */

/**
 * DFT-0044 slice V (F1, D43): mirrors the domain's own guard exactly (BayHireRequestService
 * `vendorUpdate`) - "task status is not READY" blocks, so an unclaimed request (`READY`) or one
 * whose workflow never started (`currentTaskStatus` null, `FAILED_TO_START`) both stay editable,
 * while `CLAIMED`/`COMPLETED`/anything Task Management adds later do not. `status === "REQUESTED"`
 * is checked too even though the domain does not gate on it directly - PRE_APPROVED/PAYED/etc.
 * never carry a READY task, but this keeps the guard self-explanatory rather than relying on that
 * as an implicit invariant.
 */
export const canUpdateBayHireRequest = (
  request: Pick<BayHireRequest, "status" | "currentTaskStatus">,
): boolean =>
  request.status === "REQUESTED" &&
  (request.currentTaskStatus == null || request.currentTaskStatus === "READY");

/**
 * Resubmit is offered only on a rejected request.
 *
 * Added when Resubmit moved out of the rejection banner and into the header action row, so that
 * it is guarded the same way as every sibling action rather than by an inline `status === ...`
 * check at the render site. The role restriction (Shipping Agent only) stays at the call site,
 * exactly as it does for the other vendor actions - these guards describe the REQUEST's state,
 * never the caller's identity.
 */
export const canResubmitBayHireRequest = (request: Pick<BayHireRequest, "status">): boolean =>
  request.status === "REJECTED";

/** DFT-0010: Cancel is unavailable once a request has reached a terminal outcome. */
export const canCancelBayHireRequest = (request: Pick<BayHireRequest, "status">): boolean =>
  request.status !== "CANCELLED" && request.status !== "APPROVED";

/** DFT-0014: a payment receipt may be uploaded once, only while awaiting one. */
export const canUploadBayHirePaymentReceipt = (
  request: Pick<BayHireRequest, "status" | "receipt">,
): boolean => request.status === "PRE_APPROVED" && !request.receipt;

/**
 * DFT-0016 (DFE-BAY-HIRE-004): a hire may be renewed only while `APPROVED` and within
 * the {@link RENEWAL_WINDOW_DAYS}-day pre-expiry window, inclusive of the expiry day
 * itself and exclusive once expiry has passed - matches the epic's own acceptance
 * criteria ("visible on expiry day", "hidden when > 30 days before expiry", "renewal
 * from expired requests is not allowed").
 *
 * `dateToApproved` is nullable on the type (nullable "until pre-approved" per
 * `BayHireRequest.java`), but is guaranteed set by the time a request reaches
 * `APPROVED` - verified during DFT-0016 planning (`working/05-research.md`) that
 * every path to `APPROVED` passes through `PRE_APPROVED` first, which is where it is
 * populated. The null check here is defensive, not an expected runtime case.
 *
 * @param now - injectable for deterministic tests; defaults to the real current time.
 */
export const canRenewBayHireRequest = (
  request: Pick<BayHireRequest, "status" | "dateToApproved">,
  now: Date = new Date(),
): boolean => {
  if (request.status !== "APPROVED" || !request.dateToApproved) {
    return false;
  }
  const remaining = daysUntilExpiry(request.dateToApproved, now);
  return remaining >= 0 && remaining <= RENEWAL_WINDOW_DAYS;
};
